import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaCartService } from '@app/akita/cart/state/cart.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState, ScreenSizes } from '@app/akita/screen/state/screen.store';
import { logShowAddToCart } from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { PriceModel } from '@app/shared/models/api/price.model';
import { BehaviorSubject, Observable, Subject, Subscription, map, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';

import { TranslocoModule } from '@ngneat/transloco';
// Components
import { PictureComponent } from '@app/shared/components/picture/picture.component';
import { AmountPipe } from '@app/shared/pipes/amount.pipe';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
// eslint-disable-next-line max-len
import { FeaturedDiscountBadgeComponent } from '@app/featured/components/featured-discount-badge/featured-discount-badge.component';
import { ProductJsonLdComponent } from '@app/products/components/product-json-ld/product-json-ld.component';
import { ProductReviewsStatsComponent } from '@app/products/components/product-reviews-stats/product-reviews-stats.component';
import { InstallmentAmountPipe } from '@app/shared/pipes/installment-amount.pipe';

@Component({
  selector: 'app-best-seller-card',
  templateUrl: './best-seller-card.template.html',
  styleUrls: ['./best-seller-card.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    ProductJsonLdComponent,
    AmountPipe,
    PopsySkeletonComponent,
    PictureComponent,
    FeaturedDiscountBadgeComponent,
    ProductReviewsStatsComponent,
    InstallmentAmountPipe,
  ],
})
export class BestSellerCardComponent implements OnChanges, OnDestroy {
  @Input() public product?: Product | null;
  @Input() public showFlags?: boolean | null = true;
  @Input() public bestSeller?: boolean | null = false;
  @Input() public linkToCheckout?: boolean | null = false;
  @Input() public smallerThumb?: boolean | null = false;

  public readonly isBrowser: boolean;
  public hasDiscount: boolean;

  public readonly lessThan$: Observable<ScreenSizeInfoState>;
  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public readonly screenSize$: Observable<ScreenSizes>;
  public readonly queryParamsToKeep$: Observable<Params>;
  public readonly newCheckoutParamsToKeep$: Observable<Params>;

  public readonly isRtl$: Observable<boolean>;

  private readonly priceSubject: BehaviorSubject<PriceModel | null>;
  public readonly price$: Observable<PriceModel | null>;

  private readonly urlFragmentSubject: BehaviorSubject<Array<string>>;
  public readonly urlFragment$: Observable<Array<string>>;

  public trackDeliveryUrlFragment$: Observable<Array<string>>;

  private readonly destroySubject: Subject<void>;

  private readonly subscriptions: Subscription;

  constructor(
    private readonly analytics: GoogleAnalyticsService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaCartService: AkitaCartService,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService
  ) {
    this.subscriptions = new Subscription();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.screenSize$ = this.akitaScreenQuery.selectSize();
    this.isRtl$ = this.akitaRouterQuery.selectIsRtlLayout();
    this.isBrowser = this.akitaRouterQuery.isBrowser;

    this.hasDiscount = false;

    this.destroySubject = new Subject();

    this.priceSubject = new BehaviorSubject(null as any);
    this.price$ = this.priceSubject.asObservable();

    this.urlFragmentSubject = new BehaviorSubject([] as any);
    this.urlFragment$ = this.urlFragmentSubject.asObservable();

    this.trackDeliveryUrlFragment$ = this.akitaRouterQuery.selectHomeUrlFragment();

    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.newCheckoutParamsToKeep$ = this.queryParamsToKeep$.pipe(
      map((params) => {
        const p = { ...params };
        return {
          ...p,
          item_id: this.product?.id,
        };
      })
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.product) {
      this.refresh();
    }
  }

  public refresh(): void {
    this.priceSubject.next(PriceModel.fromJson(this.product?.price));

    const originalPrice = Number(`${this.product?.originalPrice?.amount || 0}`);
    const productPrice = this.product?.price?.amount || 0;
    if (originalPrice && productPrice && originalPrice > productPrice) {
      this.hasDiscount = true;
    }

    this.urlFragmentSubject.next(
      this.product?.id
        ? this.akitaRouterQuery.getProductUrlFragment(
            this.product?.id || '',
            this.product?.titleEn || ''
          )
        : []
    );
  }

  public onSwitchProductID($event: string): void {
    this.subscriptions.add(
      this.akitaProductsService
        .getProduct($event)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: () => {
            this.product = this.akitaProductsQuery.getProduct($event);
            this.refresh();
          },
          error: () => {},
        })
    );
  }

  public onBuyListing(): boolean {
    if (this.product?.id) {
      this.akitaCartService.addNewProductToCartAsync(this.product);
    }
    return false;
  }

  public onBuyNowButtonLoaded(): void {
    logShowAddToCart(this.analytics.logEventWrapper, 'detail');
  }

  public ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
